import { TaskFilterOptions } from '@griply/types';

import { FilteredTaskQueryConfig } from './build-filtered-task-query-configs';

const DJB2_SEED = 5381;

const hashParts = (parts: string[]): string => {
  let hash = DJB2_SEED;
  for (const char of parts.join('|')) {
    // djb2
    hash = ((hash << 5) + hash + char.charCodeAt(0)) | 0;
  }
  return (hash >>> 0).toString(36);
};

// Object key order and array element order must not affect the hash — both are semantically equal.
const stableStringify = (value: unknown): string =>
  JSON.stringify(value, (_key, entry: unknown) => {
    if (Array.isArray(entry)) {
      return [...entry].sort((a, b) => String(a).localeCompare(String(b)));
    }

    return entry && typeof entry === 'object'
      ? Object.fromEntries(
          Object.entries(entry as Record<string, unknown>)
            .filter(([, nested]) => nested !== undefined)
            .sort(([a], [b]) => a.localeCompare(b)),
        )
      : entry;
  }) ?? '';

const buildConfigSignature = (configs: FilteredTaskQueryConfig[]): string[] =>
  configs.flatMap((config) =>
    Object.entries(config)
      .filter(([, value]) => value !== undefined)
      // Key order must not affect the hash, or two identical filter configs dedupe to different listeners.
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
        ([entryKey, value]) =>
          `${entryKey}: ${
            value instanceof Date
              ? value.getTime().toString()
              : Array.isArray(value)
                ? [...value].sort().join(',')
                : String(value)
          }`,
      ),
  );

export const buildFilteredTaskQueryKey = (
  filterOptions: TaskFilterOptions[],
  configs: FilteredTaskQueryConfig[],
): string[] => [
  ...buildConfigSignature(configs),
  // Guards a configSignature that misses a field: the queryKey must still change with filterOptions.
  hashParts([stableStringify(filterOptions)]),
];
